import { useState, useEffect, useMemo } from 'react';
import {
	format,
	startOfDay,
	startOfMonth,
	addMonths,
	subMonths,
	isSameDay,
	isSameMonth,
	getDay,
	getDaysInMonth,
} from 'date-fns';
import { publicBookingApi } from '../../api/publicBooking';
import { ApiError } from '../../api/client';
import type { PublicBookingSlot, WaitlistPreference } from '../../types/publicBooking';

const DOW_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const WAITLIST_PREFERENCES: { value: WaitlistPreference; label: string }[] = [
	{ value: 'ANY_TIME', label: 'Any time' },
	{ value: 'MORNING', label: 'Morning' },
	{ value: 'AFTERNOON', label: 'Afternoon' },
];

interface SlotPanelInput {
	hasDate: boolean;
	isLoading: boolean;
	hasError: boolean;
	slots: PublicBookingSlot[];
	waitlistEnabled: boolean;
}

/**
 * Decides which of the three mutually exclusive panels the slot column shows.
 *
 * The distinction that matters is between a day the salon works and has no room
 * left, and a day it does not open at all. Both arrive with nothing bookable,
 * but only the first is worth waiting for: `slots: []` is a closed day — a
 * Sunday — and offering to queue for it promises something that will never come.
 * So "fully booked" requires slots to exist and all of them to be taken.
 *
 * `hasDate && !isLoading && !hasError` guards the window between picking a day
 * and its availability arriving, where `slots` is still empty but means nothing.
 */
export const resolveSlotPanel = ({
	hasDate,
	isLoading,
	hasError,
	slots,
	waitlistEnabled,
}: SlotPanelInput) => {
	const loaded = hasDate && !isLoading && !hasError;
	const isFullyBooked = loaded && slots.length > 0 && !slots.some(slot => !slot.busy);
	const showWaitlist = waitlistEnabled && isFullyBooked;
	return {
		loaded,
		isFullyBooked,
		showWaitlist,
		showGrid: loaded && slots.length > 0 && !showWaitlist,
		/** The salon is closed on this day — no grid, no waiting list. */
		showEmptyPrompt: loaded && slots.length === 0,
	};
};

interface BookingDateTimeProps {
	token: string;
	/** Scopes availability to one stylist (paid links). */
	staffId?: number;
	/** How long the chosen services and add-ons take, so short slots drop out. */
	durationMinutes?: number;
	/** The services in the basket, so "any stylist" availability counts only staff skilled for them. */
	serviceIds?: number[];
	selectedDate: Date | null;
	selectedSlot: PublicBookingSlot | null;
	onDateChange: (date: Date) => void;
	onSlotChange: (slot: PublicBookingSlot | null) => void;
	/** Offer the waiting list when a chosen day has no open time (paid links). */
	waitlistEnabled?: boolean;
	/** Non-null once the customer has joined the list for the selected day. */
	waitlist?: WaitlistPreference | null;
	onWaitlistChange?: (preference: WaitlistPreference | null) => void;
}

/**
 * Month calendar plus the time slots for whichever day is picked.
 *
 * Availability is per-day and comes from the API, so the calendar cannot know
 * ahead of time which future days have openings — every non-past day is
 * selectable and the slot panel reports the truth once the day is chosen.
 */
const BookingDateTime = ({
	token,
	staffId,
	durationMinutes,
	serviceIds,
	selectedDate,
	selectedSlot,
	onDateChange,
	onSlotChange,
	waitlistEnabled = false,
	waitlist = null,
	onWaitlistChange,
}: BookingDateTimeProps) => {
	const today = useMemo(() => startOfDay(new Date()), []);
	// The caller hands us a fresh array each render; refetch only when its
	// contents change, not its identity.
	const serviceKey = (serviceIds ?? []).join(',');
	const stableServiceIds = useMemo(
		() => (serviceKey ? serviceKey.split(',').map(Number) : undefined),
		[serviceKey]
	);
	const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
		startOfMonth(selectedDate ?? today)
	);
	const [slots, setSlots] = useState<PublicBookingSlot[]>([]);
	// Starts true when a date is already chosen — on a remount (stepping back
	// from Review) there is a render before the fetch effect runs where `slots`
	// is still empty. Treating that as "loaded and not full" would make the
	// guard below throw away a waiting-list request the customer just made.
	const [isLoadingSlots, setIsLoadingSlots] = useState(() => selectedDate !== null);
	const [slotsError, setSlotsError] = useState<string | null>(null);

	// Load the picked day's slots. `cancelled` guards against a slower earlier
	// request landing after a newer one when the customer clicks around.
	useEffect(() => {
		if (!selectedDate) {
			setSlots([]);
			setSlotsError(null);
			return;
		}

		let cancelled = false;
		const load = async () => {
			setIsLoadingSlots(true);
			setSlotsError(null);
			try {
				const data = await publicBookingApi.getAvailability(
					token,
					format(selectedDate, 'yyyy-MM-dd'),
					{ staffId, durationMinutes, serviceIds: stableServiceIds }
				);
				if (!cancelled) setSlots(data.slots ?? []);
			} catch (error) {
				if (cancelled) return;
				setSlots([]);
				setSlotsError(
					error instanceof ApiError && error.message
						? error.message
						: 'Could not load available times. Please try again.'
				);
			} finally {
				if (!cancelled) setIsLoadingSlots(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
		// Refetches when the basket grows or shrinks: what fits the day is a
		// function of how long the job is, and of who can do it.
	}, [token, staffId, selectedDate, durationMinutes, stableServiceIds]);

	// Blank leading cells so the 1st lands under the right weekday.
	const leadingBlanks = getDay(visibleMonth);
	const daysInMonth = getDaysInMonth(visibleMonth);
	const canGoBack = !isSameMonth(visibleMonth, today) && visibleMonth > today;

	const handleDayClick = (day: number) => {
		const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day);
		onDateChange(date);
		onSlotChange(null);
		// A waiting-list request is for one specific day; moving the day makes
		// the earlier request meaningless rather than transferable.
		onWaitlistChange?.(null);
	};

	const {
		loaded: hasSlotsLoaded,
		isFullyBooked,
		showWaitlist,
		showGrid,
		showEmptyPrompt,
	} = resolveSlotPanel({
		hasDate: Boolean(selectedDate),
		isLoading: isLoadingSlots,
		hasError: Boolean(slotsError),
		slots,
		waitlistEnabled,
	});
	const hasJoinedWaitlist = waitlist !== null;

	// A time that fitted a shorter basket can stop fitting once a service is
	// added, and the reloaded list simply won't offer it any more. Drop the pick
	// rather than letting the customer book a slot the day can no longer hold.
	useEffect(() => {
		if (!selectedSlot || !hasSlotsLoaded) return;
		const stillOpen = slots.some(slot => slot.time === selectedSlot.time && !slot.busy);
		if (!stillOpen) onSlotChange(null);
	}, [slots, selectedSlot, hasSlotsLoaded, onSlotChange]);

	// A waiting-list request only makes sense for a day with nothing left. If
	// availability changed under the customer — a cancellation freed a slot, or
	// they went back and picked a different stylist — drop the request rather
	// than submitting one for a day they could simply book.
	useEffect(() => {
		if (waitlist !== null && hasSlotsLoaded && !isFullyBooked) {
			onWaitlistChange?.(null);
		}
	}, [waitlist, hasSlotsLoaded, isFullyBooked, onWaitlistChange]);

	return (
		<>
			<div className="pb-dt">
				<div>
					<div className="pb-dt__cal-nav">
						<button
							type="button"
							className="pb-dt__cal-btn"
							onClick={() => setVisibleMonth(month => subMonths(month, 1))}
							disabled={!canGoBack}
							aria-label="Previous month"
						>
							&#8249;
						</button>
						<div className="pb-dt__cal-month">{format(visibleMonth, 'MMMM yyyy')}</div>
						<button
							type="button"
							className="pb-dt__cal-btn"
							onClick={() => setVisibleMonth(month => addMonths(month, 1))}
							aria-label="Next month"
						>
							&#8250;
						</button>
					</div>

					<div className="pb-dt__cal-grid">
						{DOW_LABELS.map(label => (
							<div key={label} className="pb-dt__dow">
								{label}
							</div>
						))}
						{Array.from({ length: leadingBlanks }, (_, index) => (
							<div key={`blank-${index}`} />
						))}
						{Array.from({ length: daysInMonth }, (_, index) => {
							const day = index + 1;
							const date = new Date(
								visibleMonth.getFullYear(),
								visibleMonth.getMonth(),
								day
							);
							const isPast = date < today;
							const isToday = isSameDay(date, today);
							const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
							return (
								<button
									key={day}
									type="button"
									className={`pb-dt__day${isToday ? ' is-today' : ''}${
										isSelected ? ' is-selected' : ''
									}`}
									disabled={isPast}
									onClick={() => handleDayClick(day)}
									aria-label={format(date, 'EEEE, MMMM d, yyyy')}
								>
									{day}
								</button>
							);
						})}
					</div>
				</div>

				<div>
					<div className="pb-dt__slots-label">
						{selectedDate
							? format(selectedDate, 'EEEE, MMMM d, yyyy')
							: 'Select a date to see available times'}
					</div>

					{!selectedDate && (
						<div className="pb-dt__prompt">Pick a date from the calendar.</div>
					)}

					{selectedDate && isLoadingSlots && (
						<div className="pb-dt__prompt">Loading available times…</div>
					)}

					{selectedDate && !isLoadingSlots && slotsError && (
						<div className="pb__error-banner">
							<span>⚠️</span>
							<span>{slotsError}</span>
						</div>
					)}

					{showEmptyPrompt && (
						<div className="pb-dt__prompt">No slots available on this date.</div>
					)}

					{showWaitlist && (
						<div className={`pb-wl${hasJoinedWaitlist ? ' is-joined' : ''}`}>
							<div className="pb-wl__head">
								<span className="pb-wl__icon">
									{hasJoinedWaitlist ? '✓' : '📋'}
								</span>
								<span className="pb-wl__title">
									{hasJoinedWaitlist ? 'On the waiting list' : 'Fully booked'}
								</span>
							</div>

							{hasJoinedWaitlist ? (
								<>
									<div className="pb-wl__text">
										You&apos;re on the list for{' '}
										{selectedDate && format(selectedDate, 'EEEE, MMMM d')}.
										Continue to send your request — we&apos;ll be in touch if a
										spot opens.
									</div>
									<div className="pb-wl__pref-label">Preferred time</div>
									<div className="pb-wl__pref-group">
										{WAITLIST_PREFERENCES.map(option => (
											<button
												key={option.value}
												type="button"
												className={`pb-wl__pref${
													waitlist === option.value ? ' is-selected' : ''
												}`}
												onClick={() => onWaitlistChange?.(option.value)}
											>
												{option.label}
											</button>
										))}
									</div>
									<button
										type="button"
										className="pb-wl__leave"
										onClick={() => onWaitlistChange?.(null)}
									>
										Leave the waiting list
									</button>
								</>
							) : (
								<>
									<div className="pb-wl__text">
										Every slot on this date is taken. Join the waiting list and
										we&apos;ll contact you if a spot opens — no deposit is taken
										until you confirm a time.
									</div>
									<button
										type="button"
										className="pb-wl__join"
										onClick={() => onWaitlistChange?.('ANY_TIME')}
									>
										Join the waiting list
									</button>
								</>
							)}
						</div>
					)}

					{showGrid && (
						<div className="pb-dt__slots-grid">
							{slots.map(slot => (
								<button
									key={slot.time}
									type="button"
									className={`pb-dt__slot${
										selectedSlot?.time === slot.time ? ' is-selected' : ''
									}`}
									disabled={slot.busy}
									onClick={() => onSlotChange(slot)}
								>
									{slot.label || slot.time}
								</button>
							))}
						</div>
					)}

					{/* A fully booked day with the waiting list switched off (walk-in
					    links) still needs to say so rather than show a grid of
					    struck-through times and no explanation. */}
					{!waitlistEnabled && isFullyBooked && (
						<div className="pb-dt__prompt">
							Every time on this date is taken. Please choose another day.
						</div>
					)}
				</div>
			</div>

			{selectedDate && selectedSlot && (
				<div className="pb__dt-banner">
					<span>📅</span>
					<span>
						{format(selectedDate, 'EEEE, MMMM d, yyyy')} at{' '}
						{selectedSlot.label || selectedSlot.time}
					</span>
				</div>
			)}

			{selectedDate && hasJoinedWaitlist && (
				<div className="pb__dt-banner pb__dt-banner--waitlist">
					<span>📋</span>
					<span>
						Waiting list — {format(selectedDate, 'EEEE, MMMM d, yyyy')} (
						{WAITLIST_PREFERENCES.find(p => p.value === waitlist)?.label})
					</span>
				</div>
			)}
		</>
	);
};

export default BookingDateTime;
